import { Group, Loader, Popover, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { formatGivenFamilyName } from '@bariendo/lib/human-name';
import { useDebouncedValue } from '@mantine/hooks';
import { IconLink, IconSearch } from '@tabler/icons-react';
import { ReactElement, useState } from 'react';
import { ORBIT_DISABLED, ORBIT_GRAY } from '@/theme/colors';
import {
  candidateLine,
  CONNECT_BUTTON_STYLE,
  ConnectCandidate,
  SEARCH_RESULT_COUNT,
  SUGGESTION_COUNT,
} from './ledgerConnect';
import { LedgerRow } from './ledgerRows';
import { usePatientCandidates } from './usePatientCandidates';

/** The prototype's Connect popover: suggestions from the payer's name, then a search. */

/** What the popover suggests from: a ledger row, or an import preview row (BAR-3028). */
export type ConnectTarget = Pick<LedgerRow, 'payerName' | 'amount'>;

export interface ConnectPatientPopoverProps {
  row: ConnectTarget;
  onPick: (candidate: ConnectCandidate) => void;
  /** Icon only, with this title: the Imports preview's "Choose another patient". */
  iconTitle?: string;
}

export function ConnectPatientPopover({ row, onPick, iconTitle }: ConnectPatientPopoverProps): ReactElement {
  const [opened, setOpened] = useState(false);
  const [query, setQuery] = useState('');

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      width={340}
      withinPortal
      shadow="md"
      transitionProps={{ duration: 0 }}
    >
      <Popover.Target>
        <UnstyledButton
          style={CONNECT_BUTTON_STYLE}
          title={iconTitle}
          aria-label={iconTitle}
          onClick={(event) => {
            event.stopPropagation();
            setQuery('');
            setOpened((open) => !open);
          }}
        >
          <IconLink size={12} stroke={2} />
          {!iconTitle && 'Connect'}
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown onClick={(event) => event.stopPropagation()}>
        {opened && (
          <ConnectPanel
            row={row}
            query={query}
            onQueryChange={setQuery}
            onPick={(candidate) => {
              setOpened(false);
              onPick(candidate);
            }}
          />
        )}
      </Popover.Dropdown>
    </Popover>
  );
}

function ConnectPanel({
  row,
  query,
  onQueryChange,
  onPick,
}: {
  row: ConnectTarget;
  query: string;
  onQueryChange: (query: string) => void;
  onPick: (candidate: ConnectCandidate) => void;
}): ReactElement {
  const [debouncedQuery] = useDebouncedValue(query.trim(), 300);
  const { candidates, loading, failed, retry } = usePatientCandidates(
    debouncedQuery || row.payerName || '',
    row.amount
  );
  const suggesting = !debouncedQuery;
  const shown = candidates.slice(0, suggesting ? SUGGESTION_COUNT : SEARCH_RESULT_COUNT);

  return (
    <Stack gap={8}>
      <Text fz={13} fw={600}>
        Connect patient
      </Text>
      <TextInput
        size="xs"
        type="search"
        placeholder="Search patients"
        leftSection={<IconSearch size={14} />}
        value={query}
        onChange={(event) => onQueryChange(event.currentTarget.value)}
        data-autofocus
      />
      <Text fz={12} c={ORBIT_GRAY}>
        Payer on the transaction: {row.payerName ?? '—'}
      </Text>
      {suggesting && !failed && (
        <Text fz={12} fw={500} c={ORBIT_GRAY}>
          {loading || shown.length > 0 ? 'Suggested' : 'No suggestions'}
        </Text>
      )}
      {loading ? (
        <Loader size="xs" />
      ) : (
        shown.map((candidate) => (
          <UnstyledButton
            key={candidate.patient.id}
            onClick={() => onPick(candidate)}
            style={{ padding: '4px 6px', borderRadius: 6 }}
            className="ledger-connect-option"
          >
            <Text fz={13} fw={500}>
              {formatGivenFamilyName(candidate.patient.name?.[0])}
            </Text>
            <Text fz={12} c={ORBIT_DISABLED}>
              {candidateLine(candidate)}
            </Text>
          </UnstyledButton>
        ))
      )}
      {!loading && failed && (
        <Group gap={8} role="alert">
          <Text fz={12} c={ORBIT_DISABLED}>
            Couldn't load patients.
          </Text>
          <UnstyledButton fz={12} fw={500} onClick={retry}>
            Retry
          </UnstyledButton>
        </Group>
      )}
      {!suggesting && !loading && !failed && shown.length === 0 && (
        <Group>
          <Text fz={12} c={ORBIT_DISABLED}>
            No patients found
          </Text>
        </Group>
      )}
    </Stack>
  );
}
